import $ from 'jquery';
import { BroadcastChannel } from 'broadcast-channel';

/**
 * Represents a "cercle de soin" editor.
 *
 * @param {JQuery} $container - jQuery object containing as single element to use as editor's root container
 * @constructor
 */
const CercleSoinEditor = function ($container) {
    const editor = this;
    editor.$container = $container;
    editor.id = $container.attr('id');
    editor.otherProfileType = $container.data('otherProfileType');
    editor.targetUsagerId = $container.data('targetUsagerId');
    editor.readOnly = Boolean($container.data('readOnly'));
    editor.alertHtml = $container.find('.cercle-soin-editor-alert').html();
    editor.reloadPromise = null;
    editor.profileForm = $($container.closest('fieldset')[0].form).profileForm();

    editor.initTable();
    editor.initSearch();
    editor.initBroadcastChannels();
};

/**
 * Initialize editor's table.
 */
CercleSoinEditor.prototype.initTable = function () {
    const editor = this;
    editor.table = new CercleSoinEditorTable(
        editor.$container.find('.cercle-soin-editor-table'),
        editor,
    );
};

/**
 * Initialize editor's search components (search form and search results table).
 */
CercleSoinEditor.prototype.initSearch = function () {
    const editor = this;

    if (editor.readOnly) {
        return;
    }

    editor.$searchForm = $('#' + editor.id + '-search-form');
    editor.$searchInputs = $(editor.$searchForm.prop('elements')).filter('input');
    editor.$searchResults = editor.$container.find('.cercle-soin-editor-search-results');
    editor.$searchLoading = editor.$container.find('.cercle-soin-editor-search-loading');
    editor.$searchEmptyAlert = editor.$container.find('.cercle-soin-editor-search-empty-alert');
    editor.searchData = null;
    editor.searchResultsTable = new CercleSoinEditorTable(
        editor.$searchResults.find('.cercle-soin-editor-search-results-table'),
        editor,
        () => editor.searchData,
    );

    editor.$searchForm.on('submit', function (event) {
        event.preventDefault();
        editor.submitSearch();
    });
};

/**
 * Submit search.
 */
CercleSoinEditor.prototype.submitSearch = function () {
    const editor = this;

    editor.searchStartLoading();

    if (
        ! editor.$searchInputs.filter('[type=search]').is((idx, elem) => $(elem).prop('value'))
        && ! editor.$searchInputs.filter('[type=checkbox]').is((idx, elem) => $(elem).prop('checked'))
    ) {
        editor.searchEmptyError();
        editor.searchData = null;
        editor.searchStopLoading(false);

        return;
    }

    if (! editor.$searchForm[0].checkValidity()) {
        editor.$searchInputs
            .filter((idx, elem) => elem.validity && elem.validity.valid === false)
            .addClass('is-invalid')
            .one('input', function () {
                $(this).removeClass('is-invalid');
            });

        editor.searchData = null;
        editor.searchStopLoading(false);

        return;
    }

    editor.searchData = editor.$searchForm.serialize();
    editor.searchResultsTable.reload().then(success => editor.searchStopLoading(success));
}

/**
 * Initialize editor's broadcast channels.
 */
CercleSoinEditor.prototype.initBroadcastChannels = function () {
    const editor = this;

    if (! editor.readOnly) {
        editor.bcValidateNewUsager = new BroadcastChannel('validate_usager');
        editor.bcValidateNewUsager.onmessage = function (message) {
            if (editor.$container.is(':disabled')) {
                return;
            }

            if (message.id === editor.targetUsagerId) {
                return;
            }

            if (! message.profiles.includes(editor.otherProfileType)) {
                return;
            }

            editor.searchUsager(message.id);
        };
    }

    editor.bcUpdateCercleSoin = new BroadcastChannel('update_cercle_soin');
    editor.bcUpdateCercleSoin.onmessage = function (message) {
        if (editor.$container.is(':disabled')) {
            return;
        }

        if (message.targetUsagerId !== editor.targetUsagerId) {
            return;
        }

        if (message.otherProfileType !== editor.otherProfileType) {
            return;
        }

        editor.reloadTable();
    };
};

/**
 * Fill the search inputs with an usager identifier, clear all other inputs and submit search form.
 *
 * @param {string} otherUsagerId - Usager identifier to fill the form with
 */
CercleSoinEditor.prototype.searchUsager = function (otherUsagerId) {
    const editor = this;
    editor.$searchInputs.filter('[type=search]').val(null);
    editor.$searchInputs.filter('[type=checkbox]').prop('checked', false);
    editor.$searchInputs.filter('[name=identifiant_local]').val(otherUsagerId).trigger('input');
    editor.$searchForm.trigger('submit');
};

/**
 * Hide results and show loading indicator.
 */
CercleSoinEditor.prototype.searchStartLoading = function () {
    const editor = this;
    editor.$searchResults.addClass('d-none');
    editor.$searchLoading.removeClass('d-none');
};

/**
 * Hide loading indicator and show results if search was successful.
 *
 * @param {Boolean} success - Indicates that loading was successful
 */
CercleSoinEditor.prototype.searchStopLoading = function (success) {
    const editor = this;
    editor.$searchLoading.addClass('d-none');
    if (success) {
        editor.$searchResults.removeClass('d-none');
    }
};

/**
 * Reload table after cercle de soin has been updated
 *
 * @return {JQueryPromise}
 */
CercleSoinEditor.prototype.reloadTable = function () {
    const editor = this;

    if (editor.reloadPromise) {
        return editor.reloadPromise;
    }

    const nextEventCycle = $.Deferred();
    setTimeout(() => nextEventCycle.resolve(), 0);

    return editor.reloadPromise = nextEventCycle.then(function () {
        editor.reloadPromise = null;

        if (editor.$container.is(':disabled')) {
            return null;
        }

        const promises = [];

        promises.push(editor.table.reload());

        if (! editor.readOnly) {
            promises.push(editor.searchResultsTable.reload());
        }

        return $.when(...promises);
    });
}

/**
 * Broadcast to other cercle de soin editors that a change has been made.
 */
CercleSoinEditor.prototype.broadcast = function () {
    const editor = this;
    const message = {
        targetUsagerId: editor.targetUsagerId,
        otherProfileType: editor.otherProfileType,
    };

    editor.bcUpdateCercleSoin.postMessage(message);
}

/**
 * Handle an error.
 *
 * @param {(string|XMLHttpRequest)} errorMessage - Error message or ajax request to extract it from
 */
CercleSoinEditor.prototype.error = function (errorMessage) {
    const editor = this;

    if (typeof errorMessage === 'object') {
        if (editor.getXHRException(errorMessage) === 'EmptySearchException') {
            editor.searchEmptyError();

            return;
        }

        errorMessage = editor.getXHRErrorMessage(errorMessage);
    }

    if (errorMessage) {
        const $alert = $(editor.alertHtml);
        $alert.find('.cercle-soin-editor-alert-message').text(errorMessage);
        editor.$container.prepend($alert);
    }
}

/**
 * Get exception from an XML Http Request.
 *
 * @param {XMLHttpRequest} xhr - Aajax request
 * @return {string}
 */
CercleSoinEditor.prototype.getXHRException = function (xhr) {
    if (xhr.responseJSON && xhr.responseJSON.exception) {
        return xhr.responseJSON.exception;
    }

    return '';
}

/**
 * Get error message from an XML Http Request.
 *
 * @param {XMLHttpRequest} xhr - Ajax request
 * @return {string}
 */
CercleSoinEditor.prototype.getXHRErrorMessage = function (xhr) {
    if (xhr.responseJSON) {
        if (xhr.responseJSON.message) {
            return xhr.responseJSON.message
        }
        if (xhr.responseJSON.exception) {
            return xhr.responseJSON.exception;
        }
    }

    if (xhr.responseText) {
        return xhr.responseText;
    }
    if (xhr.status) {
        return xhr.status + ' ' + xhr.statusText;
    }

    return '';
}

/**
 * Display an error about an empty search. (This can be triggered by both client and server)
 */
CercleSoinEditor.prototype.searchEmptyError = function () {
    const editor = this;

    editor.$searchEmptyAlert.removeClass('d-none');
    editor.$searchInputs.on('input', function () {
        editor.$searchEmptyAlert.addClass('d-none');
        editor.$searchInputs.off('input');
    });
}

/**
 * Represents a table within a "cercle de soin" editor.
 *
 * @param          {JQuery} $container - jQuery object containing a single <table> element
 * @param    {CercleSoinEditor} editor - Parent editor
 * @param {Function=} ajaxDataCallback - ajax.data DataTable option (can be a function, see DT reference)
 * @constructor
 */
const CercleSoinEditorTable = function ($container, editor, ajaxDataCallback) {
    const table = this;
    table.$container = $container;
    table.id = $container.attr('id');
    table.editor = editor;
    table.ajaxDataCallback = ajaxDataCallback;

    table.dt = $container.DataTable({
        paging: false,
        searching: false,
        info: false,
        ajax: function (data, callback) {
            if (table.ajaxDataCallback) {
                data = table.ajaxDataCallback(data);

                if (data === null) {
                    callback({success: false, data: []});

                    return;
                }
            }

            return $.ajax({
               url: $container.data('source'),
               data: data,
               dataType: 'json',
            }).done(function (data) {
                for (const entry of data) {
                    entry.DT_RowId = table.id + '-row-' + entry.otherUsagerId;
                }

                callback({success: true, data: data});
            }).fail(function (xhr) {
                if (xhr.status !== 0 || xhr.statusText !== 'abort') {
                    editor.error(xhr);
                }

                callback({success: false, data: []});
            });
        },
        language: $container.data('language'),
        autoWidth: false,
    });

    $container.on('draw.dt', function() {
        table.editor.profileForm.bindInputs($container.find(':input[name]'));
    });
};

/**
 * Reload table data from server.
 *
 * @return {JQueryPromise}
 */
CercleSoinEditorTable.prototype.reload = function () {
    const table = this;
    const deferred = $.Deferred();

    table.dt.ajax.reload(response => deferred.resolve(response.success));

    return deferred.promise();
};

/**
 * jQuery interface.
 *
 * $(...).cercleSoinEditor() initializes a "cercle de soin" editor for all elements in the set that have not
 * been initialized yet and returns the CercleSoinEditor instance for the first one.
 *
 * @return {CercleSoinEditor} - CercleSoinEditor instance of the first element in the set
 */
$.fn.cercleSoinEditor = function () {
    this.each(function () {
        const $container = $(this);

        if (! $container.data('cercleSoinEditor')) {
            $container.data('cercleSoinEditor', new CercleSoinEditor($container));
        }
    });

    return this.data('cercleSoinEditor');
};

// On document ready
$(function () {
    // Initialize all elements with class cercle-soin-editor
    $('.cercle-soin-editor').cercleSoinEditor();
});

export default CercleSoinEditor;
